import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import {
  Add,
  CallReceived,
  CheckCircle,
  DeleteOutline,
  Edit,
  HelpOutline,
  PlayArrow,
  Refresh,
  Sync,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useConfirmDialog } from '../ConfirmDialog';
import api from '../../services/api';
import type { AppointmentCalendar, UserWithGroups } from '../../types';

interface ChoiceOption {
  value: string;
  label: string;
}

interface SipTrunkInboundOptions {
  inboundAuthModes?: ChoiceOption[];
  inboundTransferTargetTypes?: ChoiceOption[];
}

const DEFAULT_INBOUND_OPTIONS: Required<SipTrunkInboundOptions> = {
  inboundAuthModes: [
    { value: 'ip_allowlist', label: 'IP allowlist' },
    { value: 'userpass', label: 'Username/password' },
    { value: 'ip_and_userpass', label: 'IP and username/password' },
  ],
  inboundTransferTargetTypes: [
    { value: 'user', label: 'User' },
    { value: 'group', label: 'Transfer Group' },
  ],
};

interface SipTrunkInboundSource {
  id: string;
  name: string;
  description?: string;
  asteriskEndpointId?: string;
  endpointId?: string;
  authMode: string;
  authModeDisplay?: string;
  sourceCidrs: string[];
  username?: string;
  isActive: boolean;
}

interface SipTrunkInboundRoute {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
  sipTrunkId: string;
  sipTrunkName?: string;
  aiAgentId: string;
  aiAgentName?: string;
  phoneNumberRaw: string;
  normalizedPhoneNumber?: string;
  transferTargetType?: string | null;
  transferTargetTypeDisplay?: string;
  transferTargetId?: string | null;
  appointmentCalendarId?: string | null;
  appointmentCalendarName?: string;
  scripts?: Array<{ id: string; name: string; eventType?: string; isActive?: boolean }>;
  preCallScript?: { id: string; name: string; isActive?: boolean } | null;
  preCallTimeoutMs?: number;
  preCallDefaultVariables?: Record<string, unknown>;
  isMaterializable?: boolean;
}

interface AIAgentOption {
  id: string;
  name: string;
  aiAgentProviderName?: string | null;
  transferTargetRequired?: boolean;
  appointmentCalendarRequired?: boolean;
  isActive: boolean;
}

interface TransferGroupOption {
  id: string;
  name: string;
  isActive: boolean;
  membersCount?: number;
}

interface ScriptOption {
  id: string;
  name: string;
  eventType: string;
  isActive: boolean;
}

interface InboundSourceFormState {
  id?: string;
  name: string;
  description: string;
  asteriskEndpointId: string;
  authMode: string;
  sourceCidrsText: string;
  username: string;
  password: string;
  isActive: boolean;
}

interface InboundRouteFormState {
  id?: string;
  name: string;
  description: string;
  isActive: boolean;
  aiAgentId: string;
  phoneNumberRaw: string;
  transferTargetType: string;
  transferTargetId: string;
  appointmentCalendarId: string;
  scriptIds: string[];
  preCallScriptId: string;
  preCallTimeoutMs: string;
  preCallDefaultVariablesText: string;
}

interface RouteResolutionResult {
  status: 'ok' | 'error';
  reason: string;
  routeName?: string;
  aiAgentName?: string;
  aiAgentProviderName?: string;
  aiAgentEndpointName?: string;
  normalizedPhoneNumber?: string;
  routingDecision?: {
    normalizedPhoneNumber?: string;
  };
}

interface SipTrunkInboundTabProps {
  trunkId: string | null;
}

/** Script event type accepted as an inbound route pre-call. */
const PRE_CALL_SCRIPT_EVENT_TYPE = 'pre_call';
/** Default live-caller budget for the inbound pre-call, in milliseconds. */
const INBOUND_PRE_CALL_DEFAULT_TIMEOUT_MS = 1500;
const INBOUND_PRE_CALL_MIN_TIMEOUT_MS = 200;
const INBOUND_PRE_CALL_MAX_TIMEOUT_MS = 5000;

/**
 * Parse the pre-call default variables editor text.
 *
 * @param text - JSON object typed by the operator; blank means no defaults.
 * @returns The parsed object, or `null` when the text is not a JSON object.
 */
function parsePreCallDefaults(text: string): Record<string, unknown> | null {
  if (!text.trim()) return {};
  try {
    const parsed: unknown = JSON.parse(text);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
    return null;
  } catch {
    return null;
  }
}

const EMPTY_SOURCE_FORM: InboundSourceFormState = {
  name: '',
  description: '',
  asteriskEndpointId: '',
  authMode: 'ip_allowlist',
  sourceCidrsText: '',
  username: '',
  password: '',
  isActive: true,
};

const EMPTY_ROUTE_FORM: InboundRouteFormState = {
  name: '',
  description: '',
  isActive: true,
  aiAgentId: '',
  phoneNumberRaw: '',
  transferTargetType: '',
  transferTargetId: '',
  appointmentCalendarId: '',
  scriptIds: [],
  preCallScriptId: '',
  preCallTimeoutMs: String(INBOUND_PRE_CALL_DEFAULT_TIMEOUT_MS),
  preCallDefaultVariablesText: '',
};

/**
 * Normalize paginated and non-paginated API responses into a plain array.
 */
function asArray<T>(payload: unknown): T[] {
  const data = payload as { results?: T[] } | T[];
  if (Array.isArray(data)) return data;
  if (Array.isArray((data as { results?: T[] }).results)) return (data as { results: T[] }).results;
  return [];
}

/**
 * Extract a concise human-readable API error from common DRF response shapes.
 */
function extractApiError(error: unknown, fallback: string): string {
  const responseData = (error as { response?: { data?: Record<string, unknown> } })?.response?.data;
  if (!responseData || typeof responseData !== 'object') {
    return fallback;
  }
  if (typeof responseData.detail === 'string') {
    return responseData.detail;
  }
  if (typeof responseData.error === 'string') {
    return responseData.error;
  }
  const firstValue = Object.values(responseData)[0];
  if (Array.isArray(firstValue) && firstValue.length > 0) {
    return String(firstValue[0]);
  }
  if (typeof firstValue === 'string') {
    return firstValue;
  }
  return fallback;
}

/**
 * Parse the multiline/comma-separated CIDR textarea used by inbound sources.
 */
function parseCidrs(value: string): string[] {
  return value
    .split(/[\n,]+/)
    .map((item) => item.trim())
    .filter(Boolean);
}

/**
 * Return the transfer-user label used in route target selectors.
 */
function formatUserLabel(user: UserWithGroups): string {
  const name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
  return name ? `${name} · ${user.email}` : user.email;
}

/**
 * Administrative tab for configuring inbound SIP sources, DID routes and route resolution tests.
 */
export default function SipTrunkInboundTab({ trunkId }: SipTrunkInboundTabProps) {
  const { t } = useTranslation(['pages', 'common']);
  const { confirm, DialogComponent } = useConfirmDialog();
  const [sources, setSources] = useState<SipTrunkInboundSource[]>([]);
  const [routes, setRoutes] = useState<SipTrunkInboundRoute[]>([]);
  const [options, setOptions] = useState<SipTrunkInboundOptions>(DEFAULT_INBOUND_OPTIONS);
  const [agents, setAgents] = useState<AIAgentOption[]>([]);
  const [users, setUsers] = useState<UserWithGroups[]>([]);
  const [transferGroups, setTransferGroups] = useState<TransferGroupOption[]>([]);
  const [calendars, setCalendars] = useState<AppointmentCalendar[]>([]);
  const [scripts, setScripts] = useState<ScriptOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [sourceDialogOpen, setSourceDialogOpen] = useState(false);
  const [routeDialogOpen, setRouteDialogOpen] = useState(false);
  const [sourceForm, setSourceForm] = useState<InboundSourceFormState>(EMPTY_SOURCE_FORM);
  const [routeForm, setRouteForm] = useState<InboundRouteFormState>(EMPTY_ROUTE_FORM);
  const [testDid, setTestDid] = useState('');
  const [testingRoute, setTestingRoute] = useState(false);
  const [resolutionResult, setResolutionResult] = useState<RouteResolutionResult | null>(null);

  const selectedAgent = useMemo(
    () => agents.find((agent) => agent.id === routeForm.aiAgentId) ?? null,
    [agents, routeForm.aiAgentId],
  );

  const eligibleUsers = useMemo(
    () => users.filter((user) => {
      const endpoint = user.telephonyEndpoint;
      const userIsActive = (user as UserWithGroups & { isActive?: boolean }).isActive !== false;
      return Boolean(
        userIsActive
        && endpoint?.isActive
        && (endpoint.endpointType === 'internal_extension' || endpoint.endpointType === 'external_extension'),
      );
    }),
    [users],
  );

  const activeTransferGroups = useMemo(
    () => transferGroups.filter((group) => group.isActive),
    [transferGroups],
  );

  const activeCalendars = useMemo(
    () => calendars.filter((calendar) => calendar.isActive),
    [calendars],
  );

  const activeScripts = useMemo(
    () => scripts.filter((script) => script.isActive),
    [scripts],
  );

  const activePreCallScripts = useMemo(
    () => activeScripts.filter((script) => script.eventType === PRE_CALL_SCRIPT_EVENT_TYPE),
    [activeScripts],
  );

  const loadInboundData = useCallback(async () => {
    if (!trunkId) {
      setSources([]);
      setRoutes([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [
        sourceResponse,
        routeResponse,
        optionsResponse,
        agentsResponse,
        usersResponse,
        groupsResponse,
        calendarsResponse,
        scriptsResponse,
      ] = await Promise.all([
        api.get('/api/sip-trunks/inbound-sources/', { params: { sip_trunk_id: trunkId, page_size: 500 } }),
        api.get('/api/sip-trunks/inbound-routes/', { params: { sip_trunk_id: trunkId, page_size: 500 } }),
        api.get('/api/sip-trunks/options/'),
        api.get('/api/agents/list_simple/', { params: { is_active: true, direction: 'inbound' } }),
        api.get('/api/users/', { params: { page: 1, page_size: 500, ordering: 'email' } }),
        api.get('/api/transfer-groups/', { params: { page: 1, page_size: 500 } }),
        api.get('/api/appointments/calendars/', { params: { page: 1, page_size: 500, is_active: true } }),
        api.get('/api/scripts/', { params: { page: 1, page_size: 500, is_active: true } }),
      ]);
      setSources(asArray<SipTrunkInboundSource>(sourceResponse.data));
      setRoutes(asArray<SipTrunkInboundRoute>(routeResponse.data));
      setOptions({
        inboundAuthModes: optionsResponse.data?.inboundAuthModes ?? DEFAULT_INBOUND_OPTIONS.inboundAuthModes,
        inboundTransferTargetTypes: (
          optionsResponse.data?.inboundTransferTargetTypes
          ?? DEFAULT_INBOUND_OPTIONS.inboundTransferTargetTypes
        ),
      });
      setAgents(asArray<AIAgentOption>(agentsResponse.data));
      setUsers(asArray<UserWithGroups>(usersResponse.data));
      setTransferGroups(asArray<TransferGroupOption>(groupsResponse.data));
      setCalendars(asArray<AppointmentCalendar>(calendarsResponse.data));
      setScripts(asArray<ScriptOption>(scriptsResponse.data));
    } catch (requestError: unknown) {
      console.error('Error loading inbound SIP trunk data:', requestError);
      setError(extractApiError(requestError, t('pages:sipTrunks.inbound.errors.loading')));
    } finally {
      setLoading(false);
    }
  }, [trunkId, t]);

  useEffect(() => {
    loadInboundData();
  }, [loadInboundData]);

  const openCreateSource = () => {
    setSourceForm(EMPTY_SOURCE_FORM);
    setSourceDialogOpen(true);
  };

  const openEditSource = (source: SipTrunkInboundSource) => {
    setSourceForm({
      id: source.id,
      name: source.name,
      description: source.description || '',
      asteriskEndpointId: source.asteriskEndpointId || '',
      authMode: source.authMode || 'ip_allowlist',
      sourceCidrsText: (source.sourceCidrs || []).join('\n'),
      username: source.username || '',
      password: '',
      isActive: source.isActive,
    });
    setSourceDialogOpen(true);
  };

  const openCreateRoute = () => {
    setRouteForm(EMPTY_ROUTE_FORM);
    setRouteDialogOpen(true);
  };

  const openEditRoute = (route: SipTrunkInboundRoute) => {
    setRouteForm({
      id: route.id,
      name: route.name,
      description: route.description || '',
      isActive: route.isActive,
      aiAgentId: route.aiAgentId,
      phoneNumberRaw: route.phoneNumberRaw,
      transferTargetType: route.transferTargetType || '',
      transferTargetId: route.transferTargetId || '',
      appointmentCalendarId: route.appointmentCalendarId || '',
      scriptIds: (route.scripts || []).map((script) => script.id),
      preCallScriptId: route.preCallScript?.id || '',
      preCallTimeoutMs: String(route.preCallTimeoutMs ?? INBOUND_PRE_CALL_DEFAULT_TIMEOUT_MS),
      preCallDefaultVariablesText: route.preCallDefaultVariables && Object.keys(route.preCallDefaultVariables).length
        ? JSON.stringify(route.preCallDefaultVariables, null, 2)
        : '',
    });
    setRouteDialogOpen(true);
  };

  const saveSource = async () => {
    if (!trunkId) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const payload = {
        sipTrunkId: trunkId,
        name: sourceForm.name.trim(),
        description: sourceForm.description,
        asteriskEndpointId: sourceForm.asteriskEndpointId || undefined,
        authMode: sourceForm.authMode,
        sourceCidrs: parseCidrs(sourceForm.sourceCidrsText),
        username: sourceForm.username,
        password: sourceForm.password || undefined,
        isActive: sourceForm.isActive,
      };
      if (sourceForm.id) {
        await api.patch(`/api/sip-trunks/inbound-sources/${sourceForm.id}/`, payload);
      } else {
        await api.post('/api/sip-trunks/inbound-sources/', payload);
      }
      setSourceDialogOpen(false);
      setSourceForm(EMPTY_SOURCE_FORM);
      setSuccess(t('pages:sipTrunks.inbound.success.sourceSaved'));
      loadInboundData();
    } catch (requestError: unknown) {
      console.error('Error saving inbound source:', requestError);
      setError(extractApiError(requestError, t('pages:sipTrunks.inbound.errors.sourceSaving')));
    } finally {
      setSaving(false);
    }
  };

  const saveRoute = async () => {
    if (!trunkId) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const payload = {
        sipTrunkId: trunkId,
        name: routeForm.name.trim(),
        description: routeForm.description,
        isActive: routeForm.isActive,
        aiAgentId: routeForm.aiAgentId,
        phoneNumberRaw: routeForm.phoneNumberRaw,
        transferTargetType: routeForm.transferTargetType || null,
        transferTargetId: routeForm.transferTargetId || null,
        appointmentCalendarId: routeForm.appointmentCalendarId || null,
        scriptIds: routeForm.scriptIds,
        preCallScriptId: routeForm.preCallScriptId || null,
        preCallTimeoutMs: Number(routeForm.preCallTimeoutMs),
        preCallDefaultVariables: routeForm.preCallScriptId ? (preCallDefaults || {}) : {},
      };
      if (routeForm.id) {
        await api.patch(`/api/sip-trunks/inbound-routes/${routeForm.id}/`, payload);
      } else {
        await api.post('/api/sip-trunks/inbound-routes/', payload);
      }
      setRouteDialogOpen(false);
      setRouteForm(EMPTY_ROUTE_FORM);
      setSuccess(t('pages:sipTrunks.inbound.success.routeSaved'));
      loadInboundData();
    } catch (requestError: unknown) {
      console.error('Error saving inbound route:', requestError);
      setError(extractApiError(requestError, t('pages:sipTrunks.inbound.errors.routeSaving')));
    } finally {
      setSaving(false);
    }
  };

  const deleteSource = async (source: SipTrunkInboundSource) => {
    const confirmed = await confirm({
      title: t('pages:sipTrunks.inbound.confirm.deleteSourceTitle'),
      message: t('pages:sipTrunks.inbound.confirm.deleteSourceMessage', { name: source.name }),
      type: 'error',
      confirmIcon: <DeleteOutline />,
      confirmText: t('common:actions.delete'),
      cancelText: t('common:actions.cancel'),
    });

    if (!confirmed) return;

    setError(null);
    try {
      await api.delete(`/api/sip-trunks/inbound-sources/${source.id}/`);
      setSuccess(t('pages:sipTrunks.inbound.success.sourceDeleted'));
      loadInboundData();
    } catch (requestError: unknown) {
      console.error('Error deleting inbound source:', requestError);
      setError(extractApiError(requestError, t('pages:sipTrunks.inbound.errors.sourceDeleting')));
    }
  };

  const deleteRoute = async (route: SipTrunkInboundRoute) => {
    const confirmed = await confirm({
      title: t('pages:sipTrunks.inbound.confirm.deleteRouteTitle'),
      message: t('pages:sipTrunks.inbound.confirm.deleteRouteMessage', { name: route.name }),
      type: 'error',
      confirmIcon: <DeleteOutline />,
      confirmText: t('common:actions.delete'),
      cancelText: t('common:actions.cancel'),
    });

    if (!confirmed) return;

    setError(null);
    try {
      await api.delete(`/api/sip-trunks/inbound-routes/${route.id}/`);
      setSuccess(t('pages:sipTrunks.inbound.success.routeDeleted'));
      loadInboundData();
    } catch (requestError: unknown) {
      console.error('Error deleting inbound route:', requestError);
      setError(extractApiError(requestError, t('pages:sipTrunks.inbound.errors.routeDeleting')));
    }
  };

  const syncSource = async (source: SipTrunkInboundSource) => {
    setError(null);
    try {
      await api.post(`/api/sip-trunks/inbound-sources/${source.id}/sync/`);
      setSuccess(t('pages:sipTrunks.inbound.success.sourceSynced'));
      loadInboundData();
    } catch (requestError: unknown) {
      console.error('Error syncing inbound source:', requestError);
      setError(extractApiError(requestError, t('pages:sipTrunks.inbound.errors.sourceSync')));
    }
  };

  const testResolution = async () => {
    if (!trunkId || !testDid.trim()) return;
    setTestingRoute(true);
    setResolutionResult(null);
    setError(null);
    try {
      const response = await api.get('/api/sip-trunks/inbound-routes/test-resolution/', {
        params: {
          sip_trunk_id: trunkId,
          did: testDid.trim(),
        },
      });
      setResolutionResult(response.data as RouteResolutionResult);
    } catch (requestError: unknown) {
      console.error('Error testing inbound route resolution:', requestError);
      setError(extractApiError(requestError, t('pages:sipTrunks.inbound.errors.testResolution')));
    } finally {
      setTestingRoute(false);
    }
  };

  const sourceUsesIp = sourceForm.authMode === 'ip_allowlist' || sourceForm.authMode === 'ip_and_userpass';
  const sourceUsesAuth = sourceForm.authMode === 'userpass' || sourceForm.authMode === 'ip_and_userpass';
  const routeUsesTransfer = routeForm.transferTargetType === 'user' || routeForm.transferTargetType === 'group';
  const preCallDefaults = parsePreCallDefaults(routeForm.preCallDefaultVariablesText);
  const preCallTimeout = Number(routeForm.preCallTimeoutMs);
  const preCallFormValid = !routeForm.preCallScriptId || (
    preCallDefaults !== null
    && Number.isInteger(preCallTimeout)
    && preCallTimeout >= INBOUND_PRE_CALL_MIN_TIMEOUT_MS
    && preCallTimeout <= INBOUND_PRE_CALL_MAX_TIMEOUT_MS
  );
  const routeFormValid = Boolean(
    preCallFormValid
    && routeForm.name.trim()
    && routeForm.aiAgentId
    && routeForm.phoneNumberRaw.trim()
    && (!selectedAgent?.transferTargetRequired || (routeForm.transferTargetType && routeForm.transferTargetId))
    && (!routeUsesTransfer || routeForm.transferTargetId)
    && (!selectedAgent?.appointmentCalendarRequired || routeForm.appointmentCalendarId)
  );
  const sourceFormValid = Boolean(
    sourceForm.name.trim()
    && (!sourceUsesIp || parseCidrs(sourceForm.sourceCidrsText).length > 0)
    && (!sourceUsesAuth || (sourceForm.username.trim() && (sourceForm.id || sourceForm.password.trim())))
  );

  if (!trunkId) {
    return (
      <Alert severity="info">
        {t('pages:sipTrunks.inbound.saveTrunkFirst')}
      </Alert>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pb: { xs: 10, md: 0 } }}>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess(null)}>{success}</Alert>}

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="h6">{t('pages:sipTrunks.inbound.sources.title')}</Typography>
              <Tooltip title={t('pages:sipTrunks.inbound.sources.helper')} arrow>
                <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
              </Tooltip>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="outlined" startIcon={<Refresh />} onClick={loadInboundData} disabled={loading}>
                {t('common:actions.refresh')}
              </Button>
              <Button variant="contained" startIcon={<Add />} onClick={openCreateSource}>
                {t('pages:sipTrunks.inbound.sources.add')}
              </Button>
            </Box>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : sources.length === 0 ? (
            <Alert severity="info">{t('pages:sipTrunks.inbound.sources.empty')}</Alert>
          ) : (
            <Box sx={{ display: 'grid', gap: 1.5 }}>
              {sources.map((source) => (
                <Box
                  key={source.id}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1.3fr 1fr 1.2fr 120px' },
                    gap: 1.5,
                    alignItems: 'center',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1.5,
                  }}
                >
                  <Box>
                    <Typography fontWeight={600}>{source.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {source.endpointId || source.asteriskEndpointId}
                    </Typography>
                  </Box>
                  <Box>
                    <Chip label={source.authModeDisplay || source.authMode} size="small" />
                  </Box>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {(source.sourceCidrs || []).join(', ') || t('pages:sipTrunks.inbound.sources.noCidrs')}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                    <Chip
                      label={source.isActive ? t('common:status.active') : t('common:status.inactive')}
                      size="small"
                      color={source.isActive ? 'success' : 'default'}
                    />
                    <Tooltip title={t('pages:sipTrunks.actions.sync')} arrow>
                      <span>
                        <IconButton size="small" onClick={() => syncSource(source)} disabled={!source.isActive}>
                          <Sync fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={t('common:actions.edit')} arrow>
                      <IconButton size="small" onClick={() => openEditSource(source)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('common:actions.delete')} arrow>
                      <IconButton size="small" color="error" onClick={() => deleteSource(source)}>
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="h6">{t('pages:sipTrunks.inbound.routes.title')}</Typography>
              <Tooltip title={t('pages:sipTrunks.inbound.routes.helper')} arrow>
                <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
              </Tooltip>
            </Box>
            <Button variant="contained" startIcon={<Add />} onClick={openCreateRoute}>
              {t('pages:sipTrunks.inbound.routes.add')}
            </Button>
          </Box>

          {routes.length === 0 ? (
            <Alert severity="info">{t('pages:sipTrunks.inbound.routes.empty')}</Alert>
          ) : (
            <Box sx={{ display: 'grid', gap: 1.5 }}>
              {routes.map((route) => (
                <Box
                  key={route.id}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1.2fr 1fr 1fr 120px' },
                    gap: 1.5,
                    alignItems: 'center',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1.5,
                  }}
                >
                  <Box>
                    <Typography fontWeight={600}>{route.name}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                      {route.phoneNumberRaw} → {route.normalizedPhoneNumber || '-'}
                    </Typography>
                  </Box>
                  <Typography variant="body2">{route.aiAgentName || route.aiAgentId}</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {route.transferTargetType && (
                      <Chip label={route.transferTargetTypeDisplay || route.transferTargetType} size="small" />
                    )}
                    {route.appointmentCalendarName && (
                      <Chip label={route.appointmentCalendarName} size="small" variant="outlined" />
                    )}
                    {route.scripts?.map((script) => (
                      <Chip key={script.id} label={script.name} size="small" variant="outlined" />
                    ))}
                    {route.preCallScript && (
                      <Tooltip title={t('pages:sipTrunks.inbound.fields.preCallScript')} arrow>
                        <Chip
                          label={t('pages:sipTrunks.inbound.routes.preCallChip', { name: route.preCallScript.name })}
                          size="small"
                          color="info"
                          variant="outlined"
                        />
                      </Tooltip>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                    <Tooltip title={route.isMaterializable ? t('pages:sipTrunks.inbound.routes.ready') : t('pages:sipTrunks.inbound.routes.notReady')} arrow>
                      <Chip
                        icon={route.isMaterializable ? <CheckCircle sx={{ fontSize: 16 }} /> : undefined}
                        label={route.isActive ? t('common:status.active') : t('common:status.inactive')}
                        size="small"
                        color={route.isActive && route.isMaterializable ? 'success' : 'default'}
                      />
                    </Tooltip>
                    <Tooltip title={t('common:actions.edit')} arrow>
                      <IconButton size="small" onClick={() => openEditRoute(route)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('common:actions.delete')} arrow>
                      <IconButton size="small" color="error" onClick={() => deleteRoute(route)}>
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
            <Typography variant="h6">{t('pages:sipTrunks.inbound.test.title')}</Typography>
            <Tooltip title={t('pages:sipTrunks.inbound.test.helper')} arrow>
              <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
            </Tooltip>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr auto' }, gap: 1.5 }}>
            <TextField
              label={t('pages:sipTrunks.inbound.test.did')}
              value={testDid}
              onChange={(event) => setTestDid(event.target.value)}
              placeholder="+551130000000"
              fullWidth
            />
            <Button
              variant="contained"
              startIcon={testingRoute ? <CircularProgress size={18} color="inherit" /> : <PlayArrow />}
              onClick={testResolution}
              disabled={testingRoute || !testDid.trim()}
            >
              {t('pages:sipTrunks.inbound.test.action')}
            </Button>
          </Box>
          {resolutionResult && (
            <Alert severity={resolutionResult.status === 'ok' ? 'success' : 'warning'} sx={{ mt: 2 }}>
              {resolutionResult.status === 'ok'
                ? t('pages:sipTrunks.inbound.test.success', {
                  route: resolutionResult.routeName,
                  agent: resolutionResult.aiAgentName,
                })
                : t('pages:sipTrunks.inbound.test.failure', { reason: resolutionResult.reason })}
              {resolutionResult.routingDecision?.normalizedPhoneNumber && (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontFamily: 'monospace' }}>
                  {resolutionResult.routingDecision.normalizedPhoneNumber}
                </Typography>
              )}
            </Alert>
          )}
        </CardContent>
      </Card>

      <Dialog open={sourceDialogOpen} onClose={() => !saving && setSourceDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CallReceived color="primary" />
          {sourceForm.id ? t('pages:sipTrunks.inbound.sources.editTitle') : t('pages:sipTrunks.inbound.sources.createTitle')}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField
            label={t('pages:sipTrunks.inbound.fields.name')}
            value={sourceForm.name}
            onChange={(event) => setSourceForm((prev) => ({ ...prev, name: event.target.value }))}
            fullWidth
            required
            disabled={saving}
          />
          <TextField
            label={t('pages:sipTrunks.inbound.fields.description')}
            value={sourceForm.description}
            onChange={(event) => setSourceForm((prev) => ({ ...prev, description: event.target.value }))}
            fullWidth
            multiline
            minRows={2}
            disabled={saving}
          />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <TextField
              label={t('pages:sipTrunks.inbound.fields.asteriskEndpointId')}
              value={sourceForm.asteriskEndpointId}
              onChange={(event) => setSourceForm((prev) => ({ ...prev, asteriskEndpointId: event.target.value }))}
              fullWidth
              disabled={saving}
            />
            <FormControl fullWidth disabled={saving}>
              <InputLabel>{t('pages:sipTrunks.inbound.fields.authMode')}</InputLabel>
              <Select
                value={sourceForm.authMode}
                label={t('pages:sipTrunks.inbound.fields.authMode')}
                onChange={(event) => setSourceForm((prev) => ({ ...prev, authMode: event.target.value }))}
              >
                {(options.inboundAuthModes || []).map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          {sourceUsesIp && (
            <TextField
              label={t('pages:sipTrunks.inbound.fields.sourceCidrs')}
              value={sourceForm.sourceCidrsText}
              onChange={(event) => setSourceForm((prev) => ({ ...prev, sourceCidrsText: event.target.value }))}
              fullWidth
              multiline
              minRows={3}
              required
              disabled={saving}
              helperText={t('pages:sipTrunks.inbound.fields.sourceCidrsHelper')}
            />
          )}
          {sourceUsesAuth && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <TextField
                label={t('pages:sipTrunks.inbound.fields.username')}
                value={sourceForm.username}
                onChange={(event) => setSourceForm((prev) => ({ ...prev, username: event.target.value }))}
                fullWidth
                required
                disabled={saving}
              />
              <TextField
                label={t('pages:sipTrunks.inbound.fields.password')}
                value={sourceForm.password}
                onChange={(event) => setSourceForm((prev) => ({ ...prev, password: event.target.value }))}
                fullWidth
                required={!sourceForm.id}
                type="password"
                disabled={saving}
                helperText={sourceForm.id ? t('pages:sipTrunks.inbound.fields.passwordHelper') : ''}
              />
            </Box>
          )}
          <FormControlLabel
            control={(
              <Switch
                checked={sourceForm.isActive}
                onChange={(event) => setSourceForm((prev) => ({ ...prev, isActive: event.target.checked }))}
                disabled={saving}
              />
            )}
            label={t('pages:sipTrunks.inbound.fields.isActive')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSourceDialogOpen(false)} disabled={saving}>
            {t('common:actions.cancel')}
          </Button>
          <Button variant="contained" onClick={saveSource} disabled={saving || !sourceFormValid}>
            {saving ? t('common:actions.saving') : t('common:actions.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={routeDialogOpen} onClose={() => !saving && setRouteDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CallReceived color="primary" />
          {routeForm.id ? t('pages:sipTrunks.inbound.routes.editTitle') : t('pages:sipTrunks.inbound.routes.createTitle')}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <TextField
              label={t('pages:sipTrunks.inbound.fields.name')}
              value={routeForm.name}
              onChange={(event) => setRouteForm((prev) => ({ ...prev, name: event.target.value }))}
              fullWidth
              required
              disabled={saving}
            />
            <TextField
              label={t('pages:sipTrunks.inbound.fields.did')}
              value={routeForm.phoneNumberRaw}
              onChange={(event) => setRouteForm((prev) => ({ ...prev, phoneNumberRaw: event.target.value }))}
              fullWidth
              required
              disabled={saving}
              placeholder="+551130000000"
            />
          </Box>
          <TextField
            label={t('pages:sipTrunks.inbound.fields.description')}
            value={routeForm.description}
            onChange={(event) => setRouteForm((prev) => ({ ...prev, description: event.target.value }))}
            fullWidth
            multiline
            minRows={2}
            disabled={saving}
          />
          <FormControl fullWidth required disabled={saving}>
            <InputLabel>{t('pages:sipTrunks.inbound.fields.aiAgent')}</InputLabel>
            <Select
              value={routeForm.aiAgentId}
              label={t('pages:sipTrunks.inbound.fields.aiAgent')}
              onChange={(event) => setRouteForm((prev) => ({
                ...prev,
                aiAgentId: event.target.value,
                appointmentCalendarId: '',
              }))}
            >
              {agents.map((agent) => (
                <MenuItem key={agent.id} value={agent.id}>
                  {agent.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {selectedAgent && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {selectedAgent.transferTargetRequired && (
                <Chip color="warning" size="small" label={t('pages:sipTrunks.inbound.fields.transferRequired')} />
              )}
              {selectedAgent.appointmentCalendarRequired && (
                <Chip color="warning" size="small" label={t('pages:sipTrunks.inbound.fields.calendarRequired')} />
              )}
            </Box>
          )}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <FormControl fullWidth disabled={saving}>
              <InputLabel>{t('pages:sipTrunks.inbound.fields.transferTargetType')}</InputLabel>
              <Select
                value={routeForm.transferTargetType}
                label={t('pages:sipTrunks.inbound.fields.transferTargetType')}
                onChange={(event) => setRouteForm((prev) => ({
                  ...prev,
                  transferTargetType: event.target.value,
                  transferTargetId: '',
                }))}
              >
                <MenuItem value="">{t('common:common.none')}</MenuItem>
                {(options.inboundTransferTargetTypes || []).map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth disabled={saving || !routeForm.transferTargetType}>
              <InputLabel>{t('pages:sipTrunks.inbound.fields.transferTarget')}</InputLabel>
              <Select
                value={routeForm.transferTargetId}
                label={t('pages:sipTrunks.inbound.fields.transferTarget')}
                onChange={(event) => setRouteForm((prev) => ({ ...prev, transferTargetId: event.target.value }))}
              >
                {routeForm.transferTargetType === 'user' && eligibleUsers.map((user) => (
                  <MenuItem key={user.id} value={user.id}>{formatUserLabel(user)}</MenuItem>
                ))}
                {routeForm.transferTargetType === 'group' && activeTransferGroups.map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    {group.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <FormControl fullWidth disabled={saving}>
            <InputLabel>{t('pages:sipTrunks.inbound.fields.appointmentCalendar')}</InputLabel>
            <Select
              value={routeForm.appointmentCalendarId}
              label={t('pages:sipTrunks.inbound.fields.appointmentCalendar')}
              onChange={(event) => setRouteForm((prev) => ({ ...prev, appointmentCalendarId: event.target.value }))}
            >
              <MenuItem value="">{t('common:common.none')}</MenuItem>
              {activeCalendars.map((calendar) => (
                <MenuItem key={calendar.id} value={calendar.id}>{calendar.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth disabled={saving}>
            <InputLabel>{t('pages:sipTrunks.inbound.fields.scripts')}</InputLabel>
            <Select<string[]>
              multiple
              value={routeForm.scriptIds}
              onChange={(event: SelectChangeEvent<string[]>) => {
                const value = event.target.value;
                setRouteForm((prev) => ({
                  ...prev,
                  scriptIds: typeof value === 'string' ? value.split(',') : value,
                }));
              }}
              input={<OutlinedInput label={t('pages:sipTrunks.inbound.fields.scripts')} />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((scriptId) => (
                    <Chip
                      key={scriptId}
                      label={activeScripts.find((script) => script.id === scriptId)?.name || scriptId}
                      size="small"
                    />
                  ))}
                </Box>
              )}
            >
              {activeScripts.map((script) => (
                <MenuItem key={script.id} value={script.id}>{script.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth disabled={saving}>
            <InputLabel>{t('pages:sipTrunks.inbound.fields.preCallScript')}</InputLabel>
            <Select
              value={routeForm.preCallScriptId}
              label={t('pages:sipTrunks.inbound.fields.preCallScript')}
              onChange={(event) => setRouteForm((prev) => ({ ...prev, preCallScriptId: event.target.value }))}
            >
              <MenuItem value="">{t('common:common.none')}</MenuItem>
              {activePreCallScripts.map((script) => (
                <MenuItem key={script.id} value={script.id}>{script.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            type="number"
            label={t('pages:sipTrunks.inbound.fields.preCallTimeoutMs')}
            value={routeForm.preCallTimeoutMs}
            onChange={(event) => setRouteForm((prev) => ({ ...prev, preCallTimeoutMs: event.target.value }))}
            disabled={saving || !routeForm.preCallScriptId}
            inputProps={{ min: INBOUND_PRE_CALL_MIN_TIMEOUT_MS, max: INBOUND_PRE_CALL_MAX_TIMEOUT_MS, step: 100 }}
            helperText={t('pages:sipTrunks.inbound.fields.preCallTimeoutHelper', {
              min: INBOUND_PRE_CALL_MIN_TIMEOUT_MS,
              max: INBOUND_PRE_CALL_MAX_TIMEOUT_MS,
            })}
          />
          <TextField
            fullWidth
            multiline
            minRows={3}
            label={t('pages:sipTrunks.inbound.fields.preCallDefaults')}
            value={routeForm.preCallDefaultVariablesText}
            onChange={(event) => setRouteForm((prev) => ({ ...prev, preCallDefaultVariablesText: event.target.value }))}
            disabled={saving || !routeForm.preCallScriptId}
            error={Boolean(routeForm.preCallScriptId) && preCallDefaults === null}
            helperText={Boolean(routeForm.preCallScriptId) && preCallDefaults === null
              ? t('pages:sipTrunks.inbound.errors.preCallDefaultsInvalid')
              : t('pages:sipTrunks.inbound.fields.preCallDefaultsHelper')}
            placeholder='{"precall__expediente": "aberto"}'
          />
          <FormControlLabel
            control={(
              <Switch
                checked={routeForm.isActive}
                onChange={(event) => setRouteForm((prev) => ({ ...prev, isActive: event.target.checked }))}
                disabled={saving}
              />
            )}
            label={t('pages:sipTrunks.inbound.fields.isActive')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRouteDialogOpen(false)} disabled={saving}>
            {t('common:actions.cancel')}
          </Button>
          <Button variant="contained" onClick={saveRoute} disabled={saving || !routeFormValid}>
            {saving ? t('common:actions.saving') : t('common:actions.save')}
          </Button>
        </DialogActions>
      </Dialog>
      {DialogComponent}
    </Box>
  );
}
